import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useFormik } from 'formik';

import { createFilter, updateFilter } from 'api/Filter';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';
import Modal from 'components/shared/Modal/Modal';
import Button from 'components/shared/Button/Button';
import Typography from 'components/shared/Typography/Typography';

import { isDuplicateName } from '../lib/helpers';

const SaveFilterModal = ({
  isOpen,
  title,
  onCancel,
  filters,
  category,
  currentViews,
  currentView,
  items = [],
  type
}) => {
  const navigate = useNavigate();
  const [_, setSearchParams] = useSearchParams();

  const { mutate: createView, isLoading: isCreatingView } = useCustomMutation({
    mutationFn: (params) => createFilter(navigate, params),
    invalidateQueryKey: ['filters', category, type],
    successMessage: 'Filter saved',
    errorMessage: "Couldn't save filter. Please try again."
  });

  const { mutate: updateView, isLoading: isUpdatingView } = useCustomMutation({
    mutationFn: (params) => updateFilter(navigate, params),
    invalidateQueryKey: ['filters', category, type],
    successMessage: 'Filter updated',
    errorMessage: "Couldn't update filter. Please try again."
  });

  const formik = useFormik({
    initialValues: {
      name: currentView?.name || ''
    },
    validate: (values) => {
      const errors = {};
      if (!values.name) {
        errors.name = 'Name is required';
      }
      if (isDuplicateName(values.name, currentViews, items, currentView)) {
        errors.name = 'View name must be unique';
      }
      return errors;
    },
    onSubmit: (values) => {
      if (currentView) {
        updateView({
          id: currentView?.id,
          name: values.name,
          filters: { ...(currentView?.filters ? JSON.parse(currentView.filters) : {}) }
        });
      } else {
        createView({
          category,
          name: values.name,
          filters,
          type
        });
      }
      setSearchParams((prev) => {
        prev.set('view', values.name);
        return prev;
      });
      onCancel();
    }
  });

  return (
    <Modal
      customHeader={
        <div className="flex items-center gap-x-2">
          <Icon icon="save-mono" size={24} color="primary" />
          <Typography variant="p" color="primary-900" size="small">
            {title || (currentView ? 'Edit view' : 'Save view')}
          </Typography>
        </div>
      }
      isCentered
      className="min-w-[480px] max-w-[480px] sm:min-w-[90%] sm:max-w-[90%]"
      bodyClassName="bg-white flex flex-col gap-6 pt-4 pb-6 px-4"
      footerClassName="shadow-xxs px-4 py-3"
      headerClassName="shadow-xxs border-0 border-b border-neutral-100 border-solid"
      handleClose={onCancel}
      isOpen={isOpen}
      footer={
        <div className="flex w-full justify-between gap-x-3">
          <Button variant="tertiary" size="small" text="Cancel" onClick={onCancel} />
          <Button
            variant="primary"
            size="small"
            text="Save"
            onClick={formik.submitForm}
            isLoading={isCreatingView || isUpdatingView}
          />
        </div>
      }>
      <Input
        label="View name"
        name="name"
        required
        placeholder="Enter a name"
        value={formik.values.name}
        onChange={formik.handleChange}
        error={formik.errors.name}
      />
    </Modal>
  );
};

export default SaveFilterModal;
